import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Info } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Modal } from '../componentes/ui/Modal.js';
import { Pill } from '../componentes/ui/Pill.js';

interface BlocoPortal {
  id: string;
  cidr: string;
  versao: 'V4' | 'V6';
  fluxo: 'direto' | 'aprovacao';
  zonasPendentes: number;
}

interface Registro {
  ip: string;
  ptr: string | null;
  fcrdns: 'confirmado' | 'divergente' | 'nao_verificado';
}

export function MeusIps() {
  const [blocoAtivo, setBlocoAtivo] = useState<string | null>(null);
  const [editando, setEditando] = useState<Registro | null>(null);
  const qc = useQueryClient();

  const { data: resumo } = useQuery({
    queryKey: ['portal', 'resumo'],
    queryFn: () => api.get<{ blocos: BlocoPortal[] }>('/portal/resumo'),
  });

  const blocos = resumo?.blocos ?? [];
  const atual = blocos.find((b) => b.id === blocoAtivo) ?? blocos[0];

  const { data: pagina, isLoading } = useQuery({
    queryKey: ['ptr', atual?.id],
    queryFn: () => api.get<{ registros: Registro[]; enumeravel: boolean }>(
      `/blocos/${atual!.id}/ptr?limite=256`,
    ),
    enabled: !!atual,
  });

  if (blocos.length === 0) {
    return (
      <div className="rounded-card border border-borda bg-superficie px-6 py-14 text-center">
        <Info size={24} className="mx-auto mb-2 text-texto-mudo" />
        <p className="text-[15px] font-extrabold">Nenhum bloco atribuído</p>
        <p className="mx-auto mt-1 max-w-[400px] text-[13px] text-texto-suave">
          Quando a Consult Networks cadastrar os seus blocos de IP, eles aparecem aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Chips por bloco, como no desenho: o cliente costuma ter poucos, e
          uma lista curta é mais rápida que um seletor. */}
      <div className="flex flex-wrap gap-2">
        {blocos.map((b) => {
          const ativo = b.id === atual?.id;
          return (
            <button
              key={b.id}
              onClick={() => setBlocoAtivo(b.id)}
              className={`flex h-10 items-center gap-2 rounded-controle border px-4 font-mono text-[13px] font-bold transition-colors ${
                ativo
                  ? 'border-marca bg-tint text-marca'
                  : 'border-borda bg-superficie text-texto-medio hover:border-marca'
              }`}
            >
              {b.cidr}
              {b.fluxo === 'aprovacao' && (
                <span className="rounded-pill bg-aviso-fundo px-1.5 py-px font-sans text-[10.5px] text-aviso">
                  aprovação
                </span>
              )}
            </button>
          );
        })}
      </div>

      {atual?.fluxo === 'aprovacao' && (
        // Dito antes de o cliente clicar em editar, e não depois: descobrir que
        // a alteração virou pedido só ao salvar é frustrante.
        <div className="flex items-start gap-2.5 rounded-card border border-aviso/20 bg-aviso-fundo px-4 py-3 text-[13px] text-aviso">
          <Info size={16} className="mt-0.5 shrink-0" />
          <p>
            Alterações neste bloco passam por aprovação da Consult Networks. Você envia a
            solicitação e acompanha na aba <strong>Solicitações</strong>.
          </p>
        </div>
      )}

      {atual && atual.zonasPendentes > 0 && (
        <div className="flex items-start gap-2.5 rounded-card border border-erro/20 bg-erro-fundo px-4 py-3 text-[13px] text-erro">
          <Info size={16} className="mt-0.5 shrink-0" />
          <p>
            A delegação reversa deste bloco ainda não está completa. Os reversos configurados aqui
            só respondem para a internet depois disso — veja a aba <strong>Delegação</strong>.
          </p>
        </div>
      )}

      {isLoading ? (
        <Esqueleto />
      ) : (
        <div className="overflow-hidden rounded-card border border-borda bg-superficie">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] border-collapse">
              <thead>
                <tr className="border-b border-borda bg-borda-tenue">
                  {['Endereço', 'Reverso', 'Validação', ''].map((h) => (
                    <th
                      key={h}
                      className="px-[18px] py-3 text-left text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(pagina?.registros ?? []).map((r) => (
                  <tr key={r.ip} className="border-t border-borda-suave hover:bg-borda-tenue">
                    <td className="px-[18px] py-[11px] font-mono text-[13px]">{r.ip}</td>
                    <td className="px-[18px] py-[11px]">
                      {r.ptr ? (
                        <span className="font-mono text-[13px] font-semibold">{r.ptr}</span>
                      ) : (
                        <span className="text-[13px] italic text-texto-mudo">— sem reverso —</span>
                      )}
                    </td>
                    <td className="px-[18px] py-[11px]">
                      {!r.ptr ? (
                        <span className="text-[13px] text-texto-mudo">—</span>
                      ) : r.fcrdns === 'confirmado' ? (
                        <Pill tom="ok">
                          <CheckCircle2 size={12} className="mr-1" /> Confirmado
                        </Pill>
                      ) : r.fcrdns === 'divergente' ? (
                        <Pill tom="aviso">Divergente</Pill>
                      ) : (
                        <Pill tom="neutro">Não verificado</Pill>
                      )}
                    </td>
                    <td className="px-[18px] py-[11px] text-right">
                      <Botao tamanho="tabela" onClick={() => setEditando(r)}>
                        Editar reverso
                      </Botao>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editando && atual && (
        <ModalEditar
          registro={editando}
          bloco={atual}
          aoFechar={() => setEditando(null)}
          aoSalvar={() => {
            void qc.invalidateQueries({ queryKey: ['ptr'] });
            void qc.invalidateQueries({ queryKey: ['portal'] });
            void qc.invalidateQueries({ queryKey: ['solicitacoes'] });
          }}
        />
      )}
    </div>
  );
}

function ModalEditar({
  registro,
  bloco,
  aoFechar,
  aoSalvar,
}: {
  registro: Registro;
  bloco: BlocoPortal;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [valor, setValor] = useState(registro.ptr ?? '');
  const pedido = bloco.fluxo === 'aprovacao';

  const enviar = useMutation({
    mutationFn: () =>
      pedido
        ? // Bloco com aprovação: cria solicitação em vez de publicar.
          api.post('/solicitacoes', { ip: registro.ip, ptrNovo: valor.trim() })
        : api.put(`/blocos/${bloco.id}/ptr`, {
            ip: registro.ip,
            ptr: valor.trim() || null,
          }),
    onSuccess: () => {
      aoSalvar();
      aoFechar();
    },
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={pedido ? 'Solicitar troca de reverso' : 'Editar reverso'}
      descricao={registro.ip}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => enviar.mutate()}
            carregando={enviar.isPending}
            disabled={pedido && valor.trim().length < 3}
          >
            {pedido ? 'Enviar solicitação' : 'Salvar e publicar'}
          </Botao>
        </>
      }
    >
      <label className="block">
        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
          Nome reverso
        </span>
        <input
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          autoFocus
          spellCheck={false}
          placeholder="host-5.rev.suaempresa.com.br"
          className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
        />
        <span className="mt-1.5 block text-[12px] leading-snug text-texto-suave">
          {pedido
            ? 'A Consult Networks revisa e publica após aprovação.'
            : 'Deixe em branco para remover o reverso deste endereço.'}
        </span>
      </label>

      {enviar.error && (
        <p className="mt-4 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
          {(enviar.error as ApiError).mensagem}
        </p>
      )}
    </Modal>
  );
}

function Esqueleto() {
  return (
    <div className="rounded-card border border-borda bg-superficie p-4">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="h-11 border-b border-borda-suave last:border-0">
          <div className="h-3 w-1/4 translate-y-4 animate-pulse rounded bg-borda" />
        </div>
      ))}
    </div>
  );
}
